import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import LinearProgress from "@material-ui/core/LinearProgress";
import Typography from "@material-ui/core/Typography";
import HelpIcon from "@material-ui/icons/Help";
import IconButton from "@material-ui/core/IconButton";

import CustomInput from "@Components/CustomInput/CustomInput";
import HoverTooltip from "@Components/Tooltip/HoverTooltip";
import CustomSelect from "@Components/CustomSelect";
import eightAxios from "configs/axiosConfig";
import { useInterval } from "utils";
import {
  GRIEVANCE_TRANSLATION,
  GRIEVANCE_TRANSLATION_RESULT,
  GRIEVANCE_LANGUAGE_CHOICES,
  GRIEVANCE_URL,
} from "configs/constants";
import { useNotifyContext } from "@Components/Notify";

const BO_LANGUAGE_SELECTION = [
  { id: "ms-MY", name: "BAHASA MALAYSIA" },
  { id: "en-US", name: "ENGLISH" },
];

const GrievanceTranslation = (props) => {
  const { passedValue, renderOnly } = props;
  const [
    passedTranslationCount,
    passedTranslatedText,
    internalStatus,
  ] = passedValue;
  const { t } = useTranslation();
  const [translatedText, setTranslatedText] = useState(null);
  const [loading, setLoading] = useState(false);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [translationCount, setTranslationCount] = useState(0);
  const [translateTo, setTranslateTo] = useState("");
  const params = useParams();
  const setNotify = useNotifyContext();

  useEffect(() => {
    if (passedTranslationCount) {
      setTranslationCount(passedTranslationCount);
    }
  }, [passedTranslationCount]);

  useEffect(() => {
    if (passedTranslatedText) {
      setTranslatedText(passedTranslatedText);
    }
  }, [passedTranslatedText]);

  useEffect(() => {
    eightAxios
      .get(GRIEVANCE_LANGUAGE_CHOICES)
      .then((response) => {
        response.data.forEach(function (option) {
          option.name = option.name.toUpperCase();
        });
        setLanguageOptions(response.data);
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  const handleSubmitTranslation = () => {
    setLoading(true);
    const payload = {
      grievance_pk: params.id,
      from_language: selectedLanguage,
      to_language: translateTo,
    };
    eightAxios
      .post(GRIEVANCE_TRANSLATION, payload)
      .then((response) => {
        setTranslatedText(response.data.status);

        const decreaseTranslationCountPayload = {
          allowed_translation_count: translationCount - 1,
          internal_status: internalStatus,
        };
        eightAxios
          .patch(
            `${GRIEVANCE_URL}${params.id}/`,
            decreaseTranslationCountPayload
          )
          .then((response) => {
            setTranslationCount(response.data.allowed_translation_count);
          })
          .catch((error) => {
            console.error(error);
          });
      })
      .catch((error) => {
        console.error(error);
        setTranslatedText("Error translating. Please try again.");
        setLoading(false);
      });
  };

  const handleTranslationConfirmation = (event) => {
    event.preventDefault();
    const payload = {
      translated_text: translatedText,
      internal_status: internalStatus,
    };
    eightAxios
      .patch(`${GRIEVANCE_URL}${params.id}/`, payload)
      .then(() => {
        setNotify({
          isOpen: true,
          message: "Translation successfully saved",
          type: "success",
        });
      })
      .catch(() => {
        setNotify({
          isOpen: true,
          message: "Couldn't save translation. Please try again.",
          type: "error",
        });
      });
  };

  useInterval(
    async () => {
      const resp = await eightAxios.get(GRIEVANCE_TRANSLATION_RESULT);
      if (resp.status === 200) {
        setTranslatedText(resp.data.result);
        setLoading(false);
      }
    },
    loading ? 2000 : null
  );

  return renderOnly ? (
    <Grid container spacing={2}>
      <Grid item container xs={12}>
        <Grid item xs={12}>
          <CustomInput
            multiline
            rows={4}
            control="input"
            label="Translated Text"
            defaultValue={translatedText}
            value={translatedText}
            name="translated_text"
            disabled
          />
        </Grid>
        {loading ? (
          <Grid item xs={12}>
            <LinearProgress />
          </Grid>
        ) : null}
      </Grid>
    </Grid>
  ) : (
    <Grid container spacing={2}>
      <Grid item container spacing={1} xs={12} alignItems="center">
        <Grid item xs={12} sm={6} md={6} lg={4}>
          <CustomSelect
            label={t("Translate From")}
            required
            options={languageOptions}
            value={selectedLanguage}
            onChange={(event) => setSelectedLanguage(event.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={4}>
          <CustomSelect
            label="Translate To"
            required
            options={BO_LANGUAGE_SELECTION}
            value={translateTo}
            onChange={(event) => setTranslateTo(event.target.value)}
          />
        </Grid>

        <Grid
          item
          container
          spacing={2}
          xs={12}
          lg={4}
          alignItems="center"
          style={{ marginTop: 8 }}
        >
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmitTranslation}
              disabled={
                loading ||
                !selectedLanguage ||
                !translateTo ||
                translationCount <= 0
              }
              style={{
                borderRadius: 0,
                top: "2px",
                textTransform: "capitalize",
              }}
            >
              {t("Translate")}
            </Button>
          </Grid>

          <Grid item style={{ position: "relative" }}>
            <Typography>
              <HoverTooltip
                title={ `${t("You have")} ${translationCount} ${t("translation left.")}`}
              >
                <IconButton>
                  <HelpIcon style={{ position: "absolute" }} />
                </IconButton>
              </HoverTooltip>
            </Typography>
          </Grid>
        </Grid>
      </Grid>

      <Grid item container xs={12}>
        <Grid item xs={12}>
          <CustomInput
            multiline
            rows={4}
            control="input"
            label="Translated Text"
            defaultValue={translatedText}
            value={translatedText}
            name="translated_text"
            disabled
          />
        </Grid>
        {loading ? (
          <Grid item xs={12}>
            <LinearProgress />
          </Grid>
        ) : null}
        <Grid item container xs={12} justify="flex-end">
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              style={{
                borderRadius: 0,
                top: "2px",
                textTransform: "capitalize",
              }}
              disabled={loading || !translatedText}
              onClick={handleTranslationConfirmation}
            >
              {t("Confirm")}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

GrievanceTranslation.propTypes = {
  passedValue: PropTypes.any,
  passedTranslateFrom: PropTypes.string,
  passedTranslatedText: PropTypes.string,
  passedTranslationCount: PropTypes.number,
  renderOnly: PropTypes.bool,
};

export default GrievanceTranslation;
